import { app } from "../App";
import { emptyInvoiceData, emptyUserWithId } from "../utils/emptyObjects";
import { normalizeFirstName, normalizeName } from "../utils/utils";
import { StreetType } from "../interfaces/enums";
import { useEffect, useState } from "react";
import { useUserContext } from "../contexts/UserContext";
import { validateProfileForm, validateUserForm } from "../utils/validations";
import {
  EmailAuthProvider,
  getAuth,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";

export interface AccountSettingsFormData {
  email: string;
  firstName: string;
  lastName: string;
  organizationName: string;
  phoneNumber: string;
  isInvoiceNeeded: boolean;
  invoiceName: string;
  invoiceTaxId: string;
  invoiceStreet: string;
  invoiceHouseNumber: string;
  invoiceLocalNumber: string;
  invoicePostalCode: string;
  invoiceCity: string;
  invoiceStreetType: StreetType;
  oldPassword: string;
  newPassword: string;
  repeatedNewPassword: string;
}

export interface AccountSettingsState {
  formData: AccountSettingsFormData;
  formErrors: Record<string, string>;
  isSaving: boolean;
}

export interface AccountSettingsActions {
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSaveProfile: () => Promise<void>;
  handleChangePassword: () => Promise<void>;
}

export const useAccountSettings = (): AccountSettingsState & AccountSettingsActions => {
  const { user, updateUser } = useUserContext();

  const [formData, setFormData] = useState<AccountSettingsFormData>({
    ...emptyUserWithId,
    // Dodatkowe pola dla formularza hasła
    oldPassword: "",
    newPassword: "",
    repeatedNewPassword: "",
    // Mapowanie pól faktury na płaską strukturę dla formularza
    invoiceName: emptyUserWithId.invoiceData.name,
    invoiceTaxId: emptyUserWithId.invoiceData.taxId,
    invoiceStreet: emptyUserWithId.invoiceData.street,
    invoiceHouseNumber: emptyUserWithId.invoiceData.houseNumber,
    invoiceLocalNumber: emptyUserWithId.invoiceData.localNumber,
    invoicePostalCode: emptyUserWithId.invoiceData.postalCode,
    invoiceCity: emptyUserWithId.invoiceData.city,
    invoiceStreetType: emptyUserWithId.invoiceData.streetType,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Inicjalizacja formularza danymi użytkownika
  useEffect(() => {
    if (user) {
      setFormData({
        email: user.email || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        organizationName: user.organizationName || "",
        phoneNumber: user.phoneNumber || "",
        isInvoiceNeeded: user.isInvoiceNeeded || false,
        invoiceName: user.invoiceData?.name || "",
        invoiceTaxId: user.invoiceData?.taxId || "",
        invoiceStreet: user.invoiceData?.street || "",
        invoiceHouseNumber: user.invoiceData?.houseNumber || "",
        invoiceLocalNumber: user.invoiceData?.localNumber || "",
        invoicePostalCode: user.invoiceData?.postalCode || "",
        invoiceCity: user.invoiceData?.city || "",
        invoiceStreetType: user.invoiceData?.streetType || StreetType.STREET,
        oldPassword: "",
        newPassword: "",
        repeatedNewPassword: "",
      });
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    const validation = validateProfileForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    setIsSaving(true);
    try {
      const updatedUser = {
        ...user,
        firstName: normalizeFirstName(formData.firstName),
        lastName: normalizeName(formData.lastName),
        organizationName: formData.organizationName,
        phoneNumber: formData.phoneNumber,
        isInvoiceNeeded: formData.isInvoiceNeeded,
        invoiceData: formData.isInvoiceNeeded
          ? {
              name: formData.invoiceName,
              taxId: formData.invoiceTaxId,
              street: formData.invoiceStreet,
              houseNumber: formData.invoiceHouseNumber,
              localNumber: formData.invoiceLocalNumber || "",
              postalCode: formData.invoicePostalCode,
              city: formData.invoiceCity,
              streetType: formData.invoiceStreetType,
            }
          : emptyInvoiceData,
      };
      const result = await updateUser(updatedUser);
      if (result.success) {
        // Sukces - dane zostały zapisane
        // Wyczyść błędy
        setFormErrors({});
      } else {
        // Błąd - ustaw błąd w formularzu
        setFormErrors({ save: result.error || "Nie udało się zapisać danych" });
      }
    } catch (error) {
      console.error("Błąd podczas zapisywania profilu:", error);
      const errorMessage = error instanceof Error ? error.message : "Wystąpił błąd podczas zapisywania";
      setFormErrors({ save: errorMessage });
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const validation = validateUserForm(formData);
    if (!validation.isValid) {
      setFormErrors(validation.errors);
      return;
    }

    setIsSaving(true);
    try {
      const auth = getAuth(app);
      const currentUser = auth.currentUser;

      if (!currentUser || !user) {
        throw new Error("Brak zalogowanego użytkownika");
      }

      // Reautentykacja użytkownika
      const credential = EmailAuthProvider.credential(user.email, formData.oldPassword);
      await reauthenticateWithCredential(currentUser, credential);

      // Aktualizacja hasła
      await updatePassword(currentUser, formData.newPassword);

      // Wyczyść pola hasła po udanej zmianie
      setFormData((prev) => ({
        ...prev,
        oldPassword: "",
        newPassword: "",
        repeatedNewPassword: "",
      }));

      alert("Hasło zostało pomyślnie zmienione");
    } catch (error) {
      console.error("Błąd zmiany hasła:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Wystąpił błąd podczas zmiany hasła";
      setFormErrors((prev) => ({ ...prev, passwordChange: errorMessage }));
    } finally {
      setIsSaving(false);
    }
  };

  return {
    formData,
    formErrors,
    isSaving,
    handleChange,
    handleSaveProfile,
    handleChangePassword,
  };
};
